import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
  MenuItem, Alert, Typography, Box, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StepUpSpinner } from "../../../../Components/Loader";
import { paymentsApi } from "./api";

type Props = {
  open: boolean;
  parentId: string | number;
  // Authoritative merchant routing country from the parent record.
  // The dialog locks the Country select to this value — a tampered
  // browser request cannot change which Worldline merchant is used.
  parentCountry: "CA" | "US";
  onClose: () => void;
  onSuccess: (paymentMethodId: number) => void;
};

const CA_PROVINCES: Array<[string, string]> = [
  ["AB","Alberta"],["BC","British Columbia"],["MB","Manitoba"],["NB","New Brunswick"],
  ["NL","Newfoundland and Labrador"],["NS","Nova Scotia"],["NT","Northwest Territories"],
  ["NU","Nunavut"],["ON","Ontario"],["PE","Prince Edward Island"],["QC","Quebec"],
  ["SK","Saskatchewan"],["YT","Yukon"],
];
const US_STATES: Array<[string, string]> = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],
  ["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],
  ["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],
  ["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],
  ["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],
  ["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],
  ["WY","Wyoming"],["DC","District of Columbia"],
];

const CA_POSTAL = /^[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z] ?\d[A-CEGHJ-NPRSTV-Z]\d$/i;
const US_ZIP    = /^\d{5}(-\d{4})?$/;

// Section heading styled to match the reference (subtle, uppercase, small letter-spacing).
const SectionHeading: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.6,
      color: "text.secondary",
      textTransform: "uppercase",
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// Compact input styling — thin borders, consistent height, matches the legacy admin look.
const inputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 1 },
  "& .MuiOutlinedInput-input": { py: 1.1 },
} as const;

const AddCardDialog: React.FC<Props> = ({ open, parentId, parentCountry, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Card
  const [cardholderName, setCardholderName] = React.useState("");
  const [cardNumber, setCardNumber] = React.useState("");
  const [expiryMonth, setExpiryMonth] = React.useState<number | "">("");
  const [expiryYear, setExpiryYear] = React.useState<number | "">("");
  const [cvv, setCvv] = React.useState("");

  // Billing
  const [billingName, setBillingName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [addr1, setAddr1] = React.useState("");
  const [addr2, setAddr2] = React.useState("");
  const [city, setCity]   = React.useState("");
  const [postal, setPostal] = React.useState("");
  // Country is locked to the parent's stored country_code — the select
  // remains visible for context, but the value is not user-editable.
  const country = parentCountry;
  const [province, setProvince] = React.useState("");

  const postalOk =
    postal.trim() === "" ? false :
    country === "CA" ? CA_POSTAL.test(postal.trim())
                     : US_ZIP.test(postal.trim());
  const emailOk = /^\S+@\S+\.\S+$/.test(email.trim());
  // Phone must be EXACTLY 10 digits — enforced client-side by the
  // digits-only sanitiser on the input and re-enforced by the backend.
  const phoneOk = /^\d{10}$/.test(phone);

  const canSubmit = !busy
    && cardholderName.trim() !== ""
    && cardNumber.replace(/\s+/g, "").length >= 12
    && !!expiryMonth && !!expiryYear
    && cvv.length >= 3
    && billingName.trim() !== ""
    && emailOk
    && phoneOk
    && addr1.trim() !== ""
    && city.trim() !== ""
    && province !== ""
    && postalOk;

  const submit = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const res = await paymentsApi.addMethod(parentId, {
        cardholder_name: cardholderName,
        card_number: cardNumber.replace(/\s+/g, ""),
        expiry_month: Number(expiryMonth),
        expiry_year: Number(expiryYear),
        cvv,
        country,
        billing: {
          name: billingName.trim(),
          email: email.trim(),
          phone: phone.replace(/\D+/g, ""),
          address_line1: addr1.trim(),
          address_line2: addr2.trim() || null,
          city: city.trim(),
          province,
          postal_code: postal.trim().toUpperCase(),
          country,
        },
      });
      onSuccess(res?.payment_method_id);
    } catch (e: any) {
      const msg = e?.message ?? "Failed to add card.";
      if (/not configured/i.test(msg)) {
        setError("Worldline payment gateway is not configured.");
      } else if (/code=19/i.test(msg) || /Customer information failed data validation/i.test(msg)) {
        setError("We couldn't save this card. Please verify the billing details and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const provinceOptions = country === "CA" ? CA_PROVINCES : US_STATES;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={busy}
      PaperProps={{ sx: { position: "relative", overflow: "hidden", borderRadius: 1.5 } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 500, pr: 6, py: 2 }}>
        Add / Choose Card
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
          sx={{ position: "absolute", right: 12, top: 10, color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <SectionHeading>New Card Details</SectionHeading>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField required fullWidth label="Card Holder Name" value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)} disabled={busy}
              InputLabelProps={{ shrink: true }} sx={inputSx}
              inputProps={{ autoComplete: "cc-name" }} />
          </Grid>
          <Grid item xs={12}>
            <TextField required fullWidth label="Card Number" value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)} disabled={busy}
              InputLabelProps={{ shrink: true }} sx={inputSx}
              inputProps={{ inputMode: "numeric", maxLength: 19, autoComplete: "cc-number" }} />
          </Grid>
          <Grid item xs={4}>
            <TextField required select fullWidth label="Expiry Month" value={expiryMonth}
              onChange={(e) => setExpiryMonth(Number(e.target.value))} disabled={busy}
              InputLabelProps={{ shrink: true }} sx={inputSx}
              SelectProps={{ displayEmpty: true, renderValue: (v: any) => v ? String(v).padStart(2, "0") : <span style={{ color: "#9aa" }}>Select</span> }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>{String(m).padStart(2, "0")}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField required select fullWidth label="Expiry Year" value={expiryYear}
              onChange={(e) => setExpiryYear(Number(e.target.value))} disabled={busy}
              InputLabelProps={{ shrink: true }} sx={inputSx}
              SelectProps={{ displayEmpty: true, renderValue: (v: any) => v ? String(v) : <span style={{ color: "#9aa" }}>Select</span> }}>
              {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField required fullWidth label="CVV" value={cvv}
              onChange={(e) => setCvv(e.target.value)} disabled={busy}
              InputLabelProps={{ shrink: true }} sx={inputSx}
              placeholder="***"
              inputProps={{ inputMode: "numeric", maxLength: 4, autoComplete: "cc-csc" }} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <SectionHeading>Billing Details</SectionHeading>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField required fullWidth label="Name" value={billingName}
                onChange={(e) => setBillingName(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                inputProps={{ autoComplete: "name", maxLength: 120 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField required fullWidth label="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                inputProps={{ inputMode: "email", autoComplete: "email", maxLength: 190 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField required fullWidth type="tel" label="Phone Number (xxxxxxxxxx)" value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D+/g, "").slice(0, 10))}
                disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                error={phone !== "" && phone.length !== 10}
                helperText={phone !== "" && phone.length !== 10 ? "Phone number must be exactly 10 digits." : undefined}
                inputProps={{ inputMode: "numeric", autoComplete: "tel", maxLength: 10, pattern: "[0-9]{10}" }} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Address Line 1" value={addr1}
                onChange={(e) => setAddr1(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                inputProps={{ maxLength: 100, autoComplete: "address-line1" }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address Line 2" value={addr2}
                onChange={(e) => setAddr2(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                inputProps={{ maxLength: 100, autoComplete: "address-line2" }} />
            </Grid>
            <Grid item xs={6}>
              <TextField required fullWidth label="City" value={city}
                onChange={(e) => setCity(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                inputProps={{ maxLength: 60, autoComplete: "address-level2" }} />
            </Grid>
            <Grid item xs={6}>
              <TextField required fullWidth
                label={country === "CA" ? "Postal Code" : "ZIP Code"}
                placeholder={country === "CA" ? "A1A 1A1" : "12345"}
                value={postal}
                onChange={(e) => setPostal(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                error={postal.trim() !== "" && !postalOk}
                helperText={postal.trim() !== "" && !postalOk
                  ? (country === "CA" ? "Format: A1A 1A1" : "Format: 12345 or 12345-6789")
                  : undefined}
                inputProps={{ maxLength: 12, autoComplete: "postal-code" }} />
            </Grid>
            <Grid item xs={6}>
              <TextField required select fullWidth label="Country" value={country}
                disabled
                helperText="Locked to the parent's country. Edit the parent to change."
                InputLabelProps={{ shrink: true }} sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], bgcolor: "#f5f5f5" } }}>
                <MenuItem value="CA">CA</MenuItem>
                <MenuItem value="US">US</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField required select fullWidth label="Province / State" value={province}
                onChange={(e) => setProvince(e.target.value)} disabled={busy}
                InputLabelProps={{ shrink: true }} sx={inputSx}
                SelectProps={{ displayEmpty: true, renderValue: (v: any) => v ? String(v) : <span style={{ color: "#9aa" }}>Select</span> }}>
                {provinceOptions.map(([code, name]) => (
                  <MenuItem key={code} value={code}>{code} — {name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          sx={{
            bgcolor: "#8bc34a",
            color: "#fff",
            fontWeight: 600,
            py: 1.25,
            borderRadius: 1,
            boxShadow: "none",
            "&:hover": { bgcolor: "#7cb342", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "#c5e1a5", color: "#fff" },
          }}
        >
          {busy ? "Adding Card…" : "Add Card"}
        </Button>
      </DialogActions>

      {busy && (
        <Box
          aria-live="polite" aria-busy="true"
          sx={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 1.5,
            bgcolor: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
            transition: "opacity 160ms ease",
          }}
        >
          <StepUpSpinner size="large" />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 1 }}>
            Securely adding your card…
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Please wait while we connect securely with Worldline.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default AddCardDialog;
